/**
 * 0.38.67 - Payment card element.
 *
 * Two payment methods on one surface (cash + card), each row carrying
 * an icon, heading, two-line description and an amount, separated by a
 * divider. Built to the Henaket payment-card spec: 368px fixed width,
 * height computed from the parts, all colours mapped to existing GT18
 * tokens.
 *
 * Every string is an independent prop so admin can set each one from
 * the form editor OR drive it from rules (set_text-style effects) and
 * button commands. The `sides` prop controls which rows render:
 *   'both'  - cash row + divider + card row (default)
 *   'cash'  - cash row only, no divider
 *   'card'  - card row only, no divider
 *   'none'  - nothing (card collapses; caller decides layout impact)
 *
 * The two SVG icons (redrawn from the Figma export, Material Symbols
 * `payments` and `credit_card`) are in the icon library since 0.47.19:
 * BuiltinIcon "cash" and "payment_card".
 *
 * All display strings are coerced to strings before render (defensive
 * against bad data reaching a JSX child - the React #31 class of
 * crash). Missing strings render as empty, not "[object Object]".
 */
import React from 'react';
import styles from './PaymentCard.module.css';
import BuiltinIcon from './BuiltinIcon';
import { cardColorVars } from '../lib/cardColors';

const _s = (v) => (v == null ? '' : (typeof v === 'string' ? v : String(v)));

// 0.47.19 - The two icons now live in the icon library (BuiltinIcon:
// "cash" and "payment_card", same drawings), so other elements can use
// them too. Drawn here at the spec's 32px in the card's icon color.
function CashIcon() {
  return <span className={styles.icon}><BuiltinIcon name="cash" size={32} /></span>;
}
function CardIcon() {
  return <span className={styles.icon}><BuiltinIcon name="payment_card" size={32} /></span>;
}

function PaymentRow({ which, heading, description, amount }) {
  return (
    <div className={styles.row}>
      {which === 'cash' ? <CashIcon /> : <CardIcon />}
      {_s(heading) !== '' && <h3 className={styles.heading}>{_s(heading)}</h3>}
      {_s(description) !== '' && <p className={styles.desc}>{_s(description)}</p>}
      {_s(amount) !== '' && <p className={styles.amount}>{_s(amount)}</p>}
    </div>
  );
}

/**
 * Props:
 *   sides            'both' | 'cash' | 'card' | 'none'
 *   cashHeading      string
 *   cashDescription  string
 *   cashAmount       string
 *   cardHeading      string
 *   cardDescription  string
 *   cardAmount       string
 *   className        optional extra class on the outer card
 */
export default function PaymentCard({
  sides = 'both',
  cashHeading, cashDescription, cashAmount,
  cardHeading, cardDescription, cardAmount,
  // 0.47.20 - The card's fill and stroke (empty: Henaket).
  cardColor = '',
  cardStroke = '',
  className = '',
}) {
  const showCash = sides === 'both' || sides === 'cash';
  const showCard = sides === 'both' || sides === 'card';
  const showDivider = sides === 'both';

  if (!showCash && !showCard) return null;

  return (
    <div className={`${styles.card} ${className}`} style={cardColorVars(cardColor, cardStroke)}>
      {showCash && (
        <PaymentRow
          which="cash"
          heading={cashHeading}
          description={cashDescription}
          amount={cashAmount}
        />
      )}
      {showDivider && <hr className={styles.divider} />}
      {showCard && (
        <PaymentRow
          which="card"
          heading={cardHeading}
          description={cardDescription}
          amount={cardAmount}
        />
      )}
    </div>
  );
}
